<template>
  <button
    v-if="!disableButton"
    id="apple-pay-button"
    :disabled="isLoading || disable"
    :class="{ 'apple-pay-button--checkout-test-b': isCheckoutVariantB }"
    class="apple-pay-button"
    data-testid="apple-pay-btn"
    @click="onApplePayButtonClicked"
  >
    <CNuxtImg
      alt="apple pay button"
      :min-height="applePayButtonMinHeight"
      :src="applePayButtonImage"
    />
  </button>
</template>

<script lang="ts" setup>
import type { UpsalePurchase } from '../interfaces/upsalePurchase.interface';
import { usePaymentInit } from '../composables/usePaymentInit';
import { PAYMENT_INIT_ACTION } from '../common/constants/paymentInit';

const props = defineProps({
  discountPrice: {
    type: Number,
    required: true,
  },
  roundedPrice: {
    type: Number,
    required: true,
  },
  chargebeeId: {
    type: String,
    required: true,
  },
  upsale: Boolean,
  disable: Boolean,
  requestPayerEmail: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits<{
  'success': []
  'buy-upsale': [payload: UpsalePurchase]
  'action': []
  'availability': [available: boolean]
}>();
const { STRIPE_KEY } = useRuntimeConfig().public;
const { $paymentMethods } = useNuxtApp();
const applePayApi = useKegelApplePayApi();
const { track } = useEventsDispatcher();
const { setLoader } = useDialogStore();
const gatewayStore = useKegelGatewayStore();
const { gatewayType } = useHelpers();
const { isCheckoutVariantB } = useCheckoutFormVariant();
const applePayButtonImage = computed(() => {
  return isCheckoutVariantB.value ? '/images/apple-pay-short.webp' : '/images/apple-pay-black-buy.webp';
});
const applePayButtonMinHeight = computed(() => {
  return isCheckoutVariantB.value ? undefined : '57px';
});

const {
  isLoading,
  instance,
  createSession,
  disableButton,
} = useApplePay(onPaymentSuccess, onAction, props.requestPayerEmail);

const { isReady, isFailed, load } = usePaymentInit({
  action: PAYMENT_INIT_ACTION.APPLE_PAY,
  initialize: openApplePaySession,
});

onMounted(load);

watch([isFailed, isReady], () => {
  if (isFailed.value) emits('availability', false);
  if (isReady.value) emits('availability', true);
});

function onAction() {
  emits('action');
}

async function openApplePaySession(isCurrent: () => boolean): Promise<void> {
  try {
    const session = await applePayApi.applePayInit({
      validation_url: 'https://apple-pay-gateway.apple.com/paymentservices/startSession',
      domain_name: globalThis.location.host,
      display_name: 'Dr. Kegel',
      key: gatewayStore.spk || STRIPE_KEY,
    });

    if (!isCurrent()) return;

    instance.value = session;
  } catch (error: unknown) {
    if ((error as { status?: number })?.status !== 409) throw error;

    emits('action');
  }
}

function onPaymentSuccess() {
  emits('success');
}

function onApplePayButtonClicked() {
  setLoader(true);
  toggleButton(true);
  track('Check-out Started', {
    type: $paymentMethods.applePay,
    gateway: gatewayType.value,
  });
  if (!(globalThis as any).ApplePaySession) {
    return;
  }

  createSession(props.discountPrice, props.roundedPrice, props.chargebeeId, props.upsale);
}

function toggleButton(value: boolean) {
  disableButton.value = value;
}
</script>

<style lang="scss" scoped>
  .apple-pay-button {
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    width: 100%;

    img {
      width: 100%;
    }

    &--checkout-test-b {
      height: 55px;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      background: #000;
      border-radius: 10px;

      img {
        display: block;
        width: 100%;
        height: 100%;
        min-height: 0;
        object-fit: contain;
      }
    }
  }
</style>
